import React, {useRef} from 'react'
import PropTypes from 'prop-types'
import {IconButton, Flex, Skeleton, Text, useMultiStyleConfig, VStack} from '@chakra-ui/react'
import {useIntl} from 'react-intl'

import loadable from '@loadable/component'
const ReviewScoreIcon = loadable(() => import('../review/ReviewScoreIcon'))

import {FavouriteFilledIcon, FavouriteIcon} from '../icons'
import {useWishlistActions} from '../../hooks/use-wishlist-actions'
import {useWishList} from '../../hooks/use-wish-list'
import {useBooleanCustomPreference} from '../../hooks/use-custom-preference'
import {useGoogleTagManager} from '../../custom-api/hooks/use-google-tag-manager'
import {item_gtm_event, polish_ecommerce_gtm_event} from '../../custom-api/layers/default'
import {getPriceData} from '../../utils/product-utils'

const ProductHeader = ({name, product}) => {
    const hideAccountPref = useBooleanCustomPreference('hideAccount')
    const hideAccount = hideAccountPref !== null ? hideAccountPref : false
    const style = useMultiStyleConfig('ProductHeader')
    const {data: wishlist} = useWishList()
    const {formatMessage} = useIntl()
    const isFavouriteLoading = useRef(false)
    const {toggleWishlistItem, isProductInWishlist} = useWishlistActions(wishlist)
    const isInWishlist = isProductInWishlist(product?.c_subModelIdentifier)
    const localizedProductName =
        product?.c_variantIdentifier || product?.name || product?.productName
    const isFavourite = isProductInWishlist(product?.c_subModelIdentifier ?? product?.productId)

    const {addLayer} = useGoogleTagManager()
    const fireAddToWishlistEvent = () => {
        addLayer(polish_ecommerce_gtm_event.bind(this))
        addLayer(
            item_gtm_event.bind(this, {
                event: 'add_to_wishlist',
                currency: product?.currency,
                items: [
                    {
                        item_name: product?.c_defaultName,
                        item_id: product?.productId || product?.id,
                        item_category: product?.c_defaultCategoryName,
                        item_variant: product?.c_variants?.[0]?.productId,
                        price: parseFloat(
                            (product?.price / (1 + parseFloat(product?.c_taxRate) / 100)).toFixed(
                                2,
                            ),
                        ),
                        fullprice: getPriceData(product)?.isOnSale ? 'reduced' : 'fullprice',
                        rating_points: '',
                        erp_category: product?.c_materialGroup,
                        prdha_category: product?.c_prdhaCategory || product?.c_prdha_category,
                        stock_status:
                            product?.inventory?.stockLevel > 0 ? 'in-stock' : 'out-of-stock', //?What to do here
                    },
                ],
            }),
        )
    }

    return (
        <Skeleton isLoaded={name} w="100%" data-testid="product-header">
            <VStack {...style.container}>
                <Flex justify="space-between" w="100%" alignItems="flex-start">
                    <Text as="h1" {...style.title}>
                        {name}
                    </Text>
                    {!hideAccount && (
                        <IconButton
                            key="wishlist-button"
                            paddingInlineStart={0}
                            paddingInlineEnd={0}
                            h="auto"
                            minWidth="auto"
                            data-testid="wishlist-button"
                            icon={
                                isInWishlist ? (
                                    <FavouriteFilledIcon
                                        {...style.favSvgSolid}
                                        data-product-code={product?.id}
                                    />
                                ) : (
                                    <FavouriteIcon
                                        {...style.favSvg}
                                        data-product-code={product?.id}
                                    />
                                )
                            }
                            onClick={async () => {
                                await toggleWishlistItem(product, isFavouriteLoading)
                                if (!isInWishlist) {
                                    fireAddToWishlistEvent()
                                }
                            }}
                            className="js-wishlist-add"
                            data-product-code={product?.id}
                            aria-label={
                                isFavourite
                                    ? formatMessage(
                                          {
                                              id: 'product_tile.assistive_msg.remove_from_wishlist',
                                              defaultMessage: 'Remove {product} from wishlist',
                                          },
                                          {product: localizedProductName},
                                      )
                                    : formatMessage(
                                          {
                                              id: 'product_tile.assistive_msg.add_to_wishlist',
                                              defaultMessage: 'Add {product} to wishlist',
                                          },
                                          {product: localizedProductName},
                                      )
                            }
                        ></IconButton>
                    )}
                </Flex>
                {product?.c_categoryName && (
                    <Text
                        // dangerouslySetInnerHTML={{__html: product?.shortDescription}}
                        {...style.description}
                    >
                        {product.c_categoryName}
                    </Text>
                )}

                <ReviewScoreIcon
                    id={product?.c_subModelIdentifier || product?.id}
                    bvData={'rating_summary'}
                />
                <Text {...style.id}>{product?.c_subModelIdentifier || product?.id}</Text>
            </VStack>
        </Skeleton>
    )
}

ProductHeader.propTypes = {
    name: PropTypes.string,
    product: PropTypes.object,
    handleWishlistItem: PropTypes.func,
    isWishlistLoading: PropTypes.bool,
}

export default ProductHeader
